import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import useSWR from 'swr'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import EmptyDatas from 'components/ui/EmptyDatas'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import { MyImage } from 'components/MyImage'
import { cdnUrl } from 'commons/cdn'
import DisplayUpdateTime from 'ui/DisplayUpdateTime'
import { PAGE_SIZE, listKey } from '../PaintingRequests/api'
import {
  ListResponse,
  STATUS_LABEL,
  PaintingRequestStatus,
  formatSubmitterName,
} from '../PaintingRequests/types'

const PREVIEW_IMAGE_COUNT = 5

const statusVariant = (
  status: number,
): 'default' | 'secondary' | 'outline' => {
  if (status === PaintingRequestStatus.SENT) return 'default'
  if (status === PaintingRequestStatus.PENDING) return 'secondary'
  return 'outline'
}

const truncate = (s: string, n = 36) => {
  const t = (s || '').replace(/\s+/g, ' ').trim()
  if (t.length <= n) return t
  return `${t.slice(0, n)}…`
}

const PaintingRequestsTab: React.FC = () => {
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])

  const goAfter = useCallback((nextAfter: string | null) => {
    if (nextAfter === null) {
      setAfter(null)
      return
    }
    setAfters((prev) => {
      const index = prev.findIndex((item) => item === nextAfter)
      if (index === -1) return [...prev, nextAfter]
      return prev
    })
    setAfter(nextAfter)
  }, [])

  const { data, isLoading, isValidating, mutate } = useSWR<ListResponse>(
    listKey({ limit: PAGE_SIZE, after }),
  )

  const nextAfter =
    data?.items?.length === PAGE_SIZE
      ? data.items[data.items.length - 1]?.id
      : undefined

  if (isLoading || (isValidating && !data)) {
    return <Loader>Load painting requests</Loader>
  }
  if (!data) return <EmptyDatas />

  return (
    <div className="flex h-[calc(100vh-150px)] flex-col">
      <div className="mb-2 flex-shrink-0">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <MyPagination
              nextAfter={nextAfter}
              after={after}
              goAfter={goAfter}
              afters={afters}
            />
            <MyRefetchButton onClick={() => mutate()} />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <Card className="flex h-full flex-col">
          <CardContent className="flex-1 overflow-hidden pb-0 pl-4 pr-0">
            <div className="h-full overflow-auto">
              {!data.items?.length ? (
                <p className="py-8 text-sm text-muted-foreground">
                  暂无绘画需求
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[56px]">#</TableHead>
                      <TableHead className="w-[100px]">状态</TableHead>
                      <TableHead className="w-[360px]">备注</TableHead>
                      <TableHead className="w-[300px]">提交人</TableHead>
                      <TableHead className="min-w-[440px]">参考图</TableHead>
                      <TableHead className="w-[160px]">创建时间</TableHead>
                      <TableHead className="w-[160px]">发起时间</TableHead>
                      <TableHead className="w-[72px]">详情</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((item, index) => {
                      const images = item.styleReferenceImages || []
                      const shown = images.slice(0, PREVIEW_IMAGE_COUNT)
                      const extra = images.length - shown.length
                      return (
                      <TableRow key={item.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(item.status)}>
                            {STATUS_LABEL[item.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] text-sm text-muted-foreground">
                          {truncate(item.desc || '') || '—'}
                        </TableCell>
                        <TableCell>{formatSubmitterName(item)}</TableCell>
                        <TableCell>
                          {shown.length ? (
                            <div className="flex flex-wrap items-center gap-2 py-1">
                              {shown.map((url, i) => (
                                <MyImage
                                  key={`${item.id}-${i}`}
                                  src={cdnUrl(url)}
                                  alt={`参考图 ${i + 1}`}
                                  className="h-28 w-28 rounded-md border border-border object-cover hover:opacity-80"
                                  lazy={false}
                                />
                              ))}
                              {extra > 0 ? (
                                <span className="text-xs text-muted-foreground">
                                  +{extra}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <DisplayUpdateTime updateTime={item.updateTime} />
                          {!item.updateTime ? '—' : null}
                        </TableCell>
                        <TableCell>
                          <DisplayUpdateTime updateTime={item.publishTime} />
                          {!item.publishTime ? '—' : null}
                        </TableCell>
                        <TableCell>
                          <Link
                            className="text-sm text-primary underline-offset-4 hover:underline"
                            to={`/admin/painting-requests/${item.id}`}
                          >
                            详情
                          </Link>
                        </TableCell>
                      </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PaintingRequestsTab
